// ============================================================
//  src/auth/useAuth.js
// ============================================================

import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "../utils/supabase";

// ── Inactivity timeout duration ───────────────────────────────
const INACTIVITY_LIMIT_MS = 15 * 60 * 1000; // 15 minutes
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

export default function useAuth() {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const loadingUser  = useRef(false);
  const loggedOut    = useRef(false);
  const inactivityTimer = useRef(null); // ← tracks inactivity timeout

  function clearError() {
    setError("");
  }

  // ──────────────────────────────────────────────────────────
  // Inactivity timer — auto logout after INACTIVITY_LIMIT_MS
  // ──────────────────────────────────────────────────────────
  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = null;
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    inactivityTimer.current = setTimeout(async () => {
      // Only auto-logout if a user is actually logged in
      if (!loggedOut.current) {
        console.info("Session expired due to inactivity.");
        loggedOut.current = true;
        setUser(null);
        try {
          await supabase.auth.signOut();
        } catch (err) {
          console.error("Auto-logout error:", err);
          loggedOut.current = false;
        }
      }
    }, INACTIVITY_LIMIT_MS);
  }, [clearInactivityTimer]);

  // Attach / detach activity listeners based on user state
  useEffect(() => {
    if (!user) {
      // No user — clear timer and remove listeners
      clearInactivityTimer();
      ACTIVITY_EVENTS.forEach((e) =>
        window.removeEventListener(e, resetInactivityTimer)
      );
      return;
    }

    // User is logged in — start timer and listen for activity
    resetInactivityTimer();
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, resetInactivityTimer, { passive: true })
    );

    return () => {
      clearInactivityTimer();
      ACTIVITY_EVENTS.forEach((e) =>
        window.removeEventListener(e, resetInactivityTimer)
      );
    };
  }, [user, resetInactivityTimer, clearInactivityTimer]);

  // ──────────────────────────────────────────────────────────
  // Load user (Auth + Profile table)
  // ──────────────────────────────────────────────────────────
  async function loadUser(updateLoadingState = false) {
    if (loadingUser.current) return;
    if (loggedOut.current) {
      if (updateLoadingState) setLoading(false);
      return;
    }
    loadingUser.current = true;

    try {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (!authUser) {
        setUser(null);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from("users")
        .select("id, name, phone, email")
        .eq("id", authUser.id)
        .single();

      if (profileError && profileError.code !== "PGRST116") {
        console.error("Profile fetch error:", profileError);
      }

      const fullUser = {
        id:          authUser.id,
        email:       authUser.email,
        createdAt:   authUser.created_at,
        lastLoginAt: authUser.last_sign_in_at,
        name:        profile?.name  || "",
        phone:       profile?.phone || "",
      };

      setUser(fullUser);

    } catch (err) {
      console.error("Load user failed:", err);
      setUser(null);
    } finally {
      loadingUser.current = false;
      if (updateLoadingState) setLoading(false);
    }
  }

  // ──────────────────────────────────────────────────────────
  // Refresh user (after profile update)
  // ──────────────────────────────────────────────────────────
  function refreshUser(updatedData) {
    setUser((prev) => ({ ...prev, ...updatedData }));
  }

  // ──────────────────────────────────────────────────────────
  // Logout
  // ──────────────────────────────────────────────────────────
  async function logout() {
    clearInactivityTimer();      // ← stop timer on manual logout
    loggedOut.current = true;
    setUser(null);
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error("Logout error:", err);
      loggedOut.current = false;
    }
  }

  // ──────────────────────────────────────────────────────────
  // Sign In
  // ──────────────────────────────────────────────────────────
  async function signIn({ email, password }) {
    loggedOut.current = false;
    setError("");

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      const friendlyMessage = getFriendlyAuthError(error.message);
      setError(friendlyMessage);
      throw new Error(friendlyMessage);
    }

    await loadUser(false);
    return data;
  }

  function getFriendlyAuthError(message) {
    const msg = message.toLowerCase();

    if (
      msg.includes("invalid login credentials") ||
      msg.includes("invalid credentials") ||
      msg.includes("email logins are disabled") ||
      msg.includes("user not found") ||
      msg.includes("no user found")
    )
      return "Invalid email or password. Please try again.";

    if (msg.includes("email not confirmed"))
      return "Please verify your email before signing in.";

    if (msg.includes("too many requests"))
      return "Too many attempts. Please wait a few minutes and try again.";

    if (msg.includes("network") || msg.includes("fetch"))
      return "Network error. Please check your connection and try again.";

    return "Something went wrong. Please try again.";
  }

  // ──────────────────────────────────────────────────────────
  // Check if email already exists
  // ──────────────────────────────────────────────────────────
  async function checkEmailExists(email) {
    const { data, error } = await supabase
      .from("users")
      .select("id")
      .eq("email", email)
      .maybeSingle();

    if (error) {
      console.error("Email check error:", error);
      throw error;
    }

    return !!data;
  }

  // ──────────────────────────────────────────────────────────
  // Sign Up
  // ──────────────────────────────────────────────────────────
  async function signUp({ email, password, name, phone }) {
    setError("");

    let emailTaken = false;
    try {
      emailTaken = await checkEmailExists(email);
    } catch (err) {
      setError("Unable to verify email. Please try again.");
      throw err;
    }

    if (emailTaken) {
      const msg = "This email is already registered. Please use a different email.";
      setError(msg);
      throw new Error(msg);
    }

    const { data, error } = await supabase.auth.signUp({ email, password });

    if (error) {
      const isDuplicate =
        error.message.toLowerCase().includes("already registered") ||
        error.message.toLowerCase().includes("user already exists");

      const msg = isDuplicate
        ? "This email is already registered. Please use a different email."
        : error.message;

      setError(msg);
      throw new Error(msg);
    }

    if (data.user && data.session) {
      const { error: profileError } = await supabase.from("users").insert({
        id:    data.user.id,
        email: data.user.email,
        name:  name  || "",
        phone: phone || "",
      });

      if (profileError) {
        console.error("Profile insert error:", profileError);
        setError(profileError.message);
        throw profileError;
      }
    }

    return data;
  }

  // ──────────────────────────────────────────────────────────
  // Listen to auth changes
  // ──────────────────────────────────────────────────────────
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        loggedOut.current = false;
        setUser(null);
        return;
      }
      if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && !loggedOut.current) {
        loadUser(false);
      }
    });

    loadUser(true);

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  return {
    user,
    loading,
    logout,
    signIn,
    signUp,
    refreshUser,
    error,
    setError,
    clearError,
  };
}